// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HotInfoPicturesProps, RouteParamsWithLanguage } from "../../../../../types/screensTypes";
import Button from "../../../../../components/ui/Button";
import ImageList from "../../../../../components/images/ImageList";
import { saveHotInfoTrack, getHotInfoById } from "../../../../../api";
import { FileInput } from "../../../../../components/common/FileInput";
import TitleSubtitle from "../../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../../context/TrackCreationContext";
import { uploadFileToS3Complete } from "../../../../../api/s3";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../../../../utils/image";
import ImageSpecsBadges from "../../../../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../../../../hooks/useImageValidation";
import stepStyles from "../../createStep.module.css";
import partnerStyles from "./PartnerEdit.module.css";

const HotInfoPictures: React.FC<HotInfoPicturesProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { hotinfoId, language } = useParams<RouteParamsWithLanguage>();
    const [pictures, setPictures] = useState<string[]>([]);
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const imageValidation = useImageValidation({
        type: "hotInfo",
        imageUrl: pictures[pictures.length - 1],
    });

    const handleFileSelected = async (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files;

        if (file) {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No authentication token found");
                return;
            }

            await imageValidation.validateImage(file);

            uploadFileToS3Complete(
                {
                    file,
                    isPublic: true,
                    isFolder: false,
                },
                token,
            )
                .then((result: string) => {
                    setPictures((prevPictures) => [...prevPictures, result]);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    };

    const handleSaveChanges = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;

        if (!hotinfoId || !trackId || !token || !language) {
            return;
        }
        await saveHotInfoTrack({
            token,
            trackId,
            id: hotinfoId,
            pictures,
        });
        navigateToNext(hotinfoId, language);
    };

    const getHotInfo = async (id: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !language) {
            return;
        }

        const hotInfoData = await getHotInfoById({ token, hotinfoId: id, language });
        if (hotInfoData && hotInfoData.pictures && hotInfoData.pictures.length > 0) {
            const picturesData = hotInfoData.pictures;
            const urls = picturesData.map((item: { url: string }) => item.url);
            setPictures(urls);
        }
    };

    useEffect(() => {
        if (hotinfoId && hotinfoId !== "") {
            getHotInfo(hotinfoId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hotinfoId, language]);

    return (
        <div className={`${stepStyles.editScreen} ${partnerStyles.screen}`}>
            <TitleSubtitle
                title={t("addPhotosOfYourPartner")}
                subtitle={t("imagesHelpVisitorsVisualizeJoiningYourActivity")}
                layout="edit"
            />

            <div className={`${stepStyles.stepDescription} ${partnerStyles.section}`}>
                <h2 className={partnerStyles.sectionHeading}>{t("requirements")}</h2>
                <ImageSpecsBadges spec="hotInfo" warnings={imageValidation.warnings} />
                <p className={partnerStyles.warn}>
                    <span className={partnerStyles.warnMark}>✕</span>
                    {t("noPhotographerWatermarksLogosOrReadableLicensePlates")}
                </p>
            </div>

            <div className={`${stepStyles.stepDescription} ${partnerStyles.section}`}>
                <div className={`${stepStyles.fileUpload} ${partnerStyles.upload}`}>
                    <h2 className={partnerStyles.sectionHeading}>{t("uploadYourPhotos")}</h2>
                    <p className={partnerStyles.sectionHelp}>
                        {t("dragYourPhotosIntoTheAreaBelowOrSelectUploadPhotos")}
                    </p>
                    <FileInput
                        onFileSelected={handleFileSelected}
                        showButton={true}
                        buttonText={t("add")}
                        acceptedFileTypes={IMAGE_FORMAT}
                        maxSize={IMAGE_MAX_SIZE_200_KB}
                        maxSizeLabel={t("imageMaxSize200kb")}
                    />
                </div>
            </div>

            <div className="photo-gallery">
                <ImageList pictures={pictures} setPictures={setPictures} />
            </div>

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={`${stepStyles.continueBtn} ${partnerStyles.continueBtn}`}
                    label={t("continue")}
                    onClick={handleSaveChanges}
                />
            </div>
        </div>
    );
};

export default HotInfoPictures;
